import { messageActions } from "reusable/mui/modals/message-actions"
import { messageInfo } from "reusable/mui/modals/message-info"
import { CustomTable } from "reusable/mui/table"
import { TrpcFetcherOptions, TrpcResponse, trpcResponseSchema } from "@gap/backend/endpoints/trpc/trpc-types"
import { orgContextCache } from "cache/orgContextCache"
import Box from "@mui/material/Box"
import { themeConfig } from '@gap/shared/theme'
import { DialogButtonOptions } from "reusable/gap-dialog/GapDialogActionButton"
import * as Sentry from "@sentry/browser"
import variables from "variables/variables"
import { GapError } from "@trpc-endpoints/trpc/error-handler/GapError"
import { forceLogoutWithReason } from "auth/logout-reason"
import { isProdLikeStage } from "@gap/utils/stage"
import { CLIENT_BUILD_HEADER, FRONTEND_OUTDATED_HEADER } from "@gap/backend/app-services/frontend-build/frontendBuildHeaders"
import { newVersionStore } from "reusable/app-version/NewVersionStore"
import { serverReachabilityStore } from "reusable/connectivity/ServerReachabilityStore"

let messagesOnDisplay: string[] = []

const displayMessage = async (error: ErrorTempType) => {
   const { actions, content, title } = error

   const key = `${error.title}-${error.content}`

   if (messagesOnDisplay.includes(key)) return

   messagesOnDisplay.push(key)
   await messageActions({ ...actions, children: content, title })
   messagesOnDisplay = messagesOnDisplay.filter(m => m !== key)
}

const NETWORK_RETRY_DELAYS_MS = [250, 750]

const delay = (milliseconds: number) => new Promise(resolve => setTimeout(resolve, milliseconds))

/** Fetches, retrying transient network failures on idempotent requests only. */
async function fetchWithNetworkRetry(url: Parameters<typeof fetch>[0], options: RequestInit): Promise<Response> {
   const isIdempotent = (options.method ?? 'GET').toUpperCase() === 'GET'
   const attemptDelays = isIdempotent ? NETWORK_RETRY_DELAYS_MS : []

   return attemptDelays.reduce<Promise<Response>>(
      (previousAttempt, retryDelay) => previousAttempt.catch(async error => {
         if (options.signal?.aborted) throw error
         await delay(retryDelay)
         return window.fetch(url, options)
      }),
      window.fetch(url, options),
   )
}

/** Reads a response body as JSON, or undefined when it is not JSON. */
async function readJsonBody(response: Response): Promise<unknown> {
   try {
      return await response.clone().json()
   } catch {
      return undefined
   }
}

/**
 * Whether a response came from in front of the API rather than from it, such as API Gateway failing to invoke it.
 * @param params The response and its body read as JSON.
 * @returns True for a server error or throttling whose body is not a tRPC response.
 */
function isServerUnreachableResponse(params: { response: Response, json: unknown }): boolean {
   const { response, json } = params
   if (response.status < 500 && response.status !== 429) return false
   return !trpcResponseSchema.safeParse(json).success
}

export async function trpcFetcher({ fetchParams, auth, cors, silent }: TrpcFetcherOptions): ReturnType<typeof window.fetch> {
   const url = fetchParams[0]
   const options = fetchParams[1] || { headers: {} } as RequestInit
   options.headers = options.headers ?? {}

   const headers = options.headers as HeadersInit & { authorization: string; orgid: string | null; 'x-time-zone'?: string; [CLIENT_BUILD_HEADER]?: string }

   const clientBuild = import.meta.env.VITE_SENTRY_RELEASE
   if (clientBuild) {
      headers[CLIENT_BUILD_HEADER] = clientBuild
   }

   if (cors) {
      options.mode = 'cors'
   }

   // attaches auth header when needed
   if (auth?.type === 'jwt') {
      headers['authorization'] = `Bearer ${auth.token}`
      headers['orgid'] = orgContextCache.authOrgId.get() || null
      try {
         headers['x-time-zone'] = Intl.DateTimeFormat().resolvedOptions().timeZone
      } catch {
         /* ignore */
      }
   }

   const startTime = Date.now()
   let response: any
   try {
      response = await fetchWithNetworkRetry(url, options)
   } catch (error: any) {
      if (!options.signal?.aborted) {
         serverReachabilityStore.reportHttpFailure()
      }
      Sentry.captureException(error, {
         extra: {
            context: error.message,
            url,
            orgId: orgContextCache.authOrgId.get() || null
         }
      })
      throw error
   }

   const endTime = Date.now()
   const duration = endTime - startTime

   if (response.headers?.get(FRONTEND_OUTDATED_HEADER) === 'true') {
      newVersionStore.announce()
   }

   const json = !response.ok || response.status === 207 ? await readJsonBody(response) : undefined
   if (isServerUnreachableResponse({ response, json })) {
      serverReachabilityStore.reportHttpFailure()
   } else {
      serverReachabilityStore.reportHttpSuccess()
   }

   // A 401 while gap tokens are present means the guest token was rejected by the API Gateway
   // JWT authorizer. The session cannot recover (the refresh endpoint sits behind the same
   // authorizer), so log out instead of letting polling loops retry forever. Checked before the
   // silent early-return on purpose: session death is global, not a per-call concern.
   if (response.status === 401 && localStorage.getItem(variables.gapAccessTokenLocalStorageKey)) {
      await forceLogoutWithReason({ reason: 'guestSessionExpired' })
      return response
   }

   if (silent) {
      return response
   }

   if (!response.ok || response.status === 207) {
      const errors: ErrorTempType[] = []

      let parsedBody: TrpcResponse = []

      const parsedResponse = trpcResponseSchema.safeParse(json)

      if (!parsedResponse.success) {
         if ((json as { error?: { json?: { message?: string } } } | undefined)?.error?.json?.message === GapError.messages.auth.loginExpired) {
            await forceLogoutWithReason({ reason: 'sessionExpired' })
         } else {
            // TODO: remove this once we have a proper error handling in the frontend
            Sentry.captureException('Unexpected trpc response', {
               extra: {
                  context: duration > 28000 ? 'Trpc network timeout' : 'Trpc unexpected network response',
                  url,
                  json,
                  duration: Math.ceil(duration / 1000),
                  orgId: orgContextCache.authOrgId.get() || null,
                  jsonString: JSON.stringify(json),
               }
            })
         }
      } else {
         parsedBody = parsedResponse.data
      }

      for (const responseItem of parsedBody) {
         if (responseItem.error) {
            const { json: { data, message, cause } } = responseItem.error

            const error: ErrorTempType = {
               content: message,
               title: "Unexpected error",
               actions: {}
            }

            switch (data.gapError?.gapErrorData.gapErrorType) {
               case 'triedUpdatingStaleRecord':
                  // if there's any stale record, we need to reload the page
                  error.actions.primary = {
                     name: "Refresh",
                     notCloseAutomatically: true,
                     callback: () => { window.location.reload() }
                  }
                  error.title = 'Reload Required'
                  break;
               case 'support':
                  error.title = 'Contact Support'
                  break
               case 'forbidden':
                  error.title = 'Forbidden'
                  break
               case 'generic':
                  error.title = 'Gap Error'
                  break
               case 'userIsDeactivated': {
                  return response
                  break
               }
            }

            if (!data.gapError) {
               if (isProdLikeStage(variables.stage)) {
                  error.actions.primary = {
                     name: 'Refresh',
                     notCloseAutomatically: true,
                     callback: () => { window.location.reload() }
                  }
                  error.actions.cancel = {
                     name: 'Close',
                  }
               } else {
                  if (!!responseItem.error) {
                     error.actions.primary = {
                        name: 'Full Error',
                        notCloseAutomatically: true,
                        callback: () =>
                           messageInfo(<Box sx={{ fontWeight: 400, color: themeConfig.primary[900], p: '5px' }}>
                              <pre style={{ borderRadius: '8px' }}>
                                 {JSON.stringify(responseItem.error, null, 2)}
                              </pre>
                           </Box>, 'Full Error', {
                              primary: {
                                 name: 'Close'
                              },
                              size: "large"
                           },),
                     }
                  }
                  const stackErrorAction = {
                     name: 'Stack Trace',
                     notCloseAutomatically: true,
                     callback: () => messageInfo(<code>{cause?.stack}</code>, 'Stack Trace', {
                        primary: {
                           name: 'Close'
                        },
                        size: 'large'
                     }),
                  }
                  if (error.actions.primary) {
                     error.actions.secondary = stackErrorAction
                  } else {
                     error.actions.primary = stackErrorAction
                  }
               }
            }

            if (!errors.find(e => e.content === error.content && e.title === error.title)) {
               errors.push(error)
            }
         }
      }

      if (errors.length === 1) {
         displayMessage(errors[0])
      } else if (errors.length > 1) {
         if (errors.length >= 4) {
            messageActions({
               DialogProps: { maxWidth: 'lg', fullWidth: true },
               primary: {
                  name: 'Ok',
               },
               children: <CustomTable
                  overrides={{
                     toolbar: () => <></>
                  }}
                  rows={errors}
                  columns={[
                     { field: 'title', title: 'Title' },
                     { field: 'content', title: 'Message' },
                  ]}
                  actions={{
                     rowActions: r => Object.values(r.actions).map(a => ({
                        title: a.name as string,
                        callback: a.callback as () => void,
                     }))
                  }}
               />,
               title: 'Errors',
            })
         } else {
            for (const error of errors) {
               await displayMessage(error)
            }
         }
      }
   }

   return response
}

type ErrorTempType = {
   content: string
   title: string
   actions: DialogButtonOptions
}
